import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 20, right: 50, bottom: 60, left: 60 };
const CHART_HEIGHT = 280;

const BAR_COLORS = {
  positive: '#16a34a',
  negative: '#dc2626',
};

const ACCEL_COLOR = '#8b5cf6';

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
  },
  headerText: {
    flex: 1,
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    margin: 0,
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    margin: '4px 0 0 0',
  },
  toggleGroup: {
    display: 'flex',
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    overflow: 'hidden',
    flexShrink: 0,
    marginLeft: '16px',
  },
  toggleButton: {
    padding: '4px 10px',
    fontSize: '11px',
    fontWeight: 500,
    border: 'none',
    cursor: 'pointer',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    transition: 'background 0.15s, color 0.15s',
  },
  toggleActive: {
    background: '#2563eb',
    color: '#fff',
  },
  toggleInactive: {
    background: '#fff',
    color: '#6b7280',
  },
  legend: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    marginBottom: '8px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    color: '#6b7280',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

export default function PriceVelocityChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [mode, setMode] = useState('dollar'); // 'dollar' | 'percent'

  useEffect(() => {
    if (!data || !data.dates || data.dates.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 600;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', CHART_HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const values = mode === 'dollar' ? data.velocity : data.velocityPercent;
    if (!values || values.length === 0) return;

    // Scales
    const x = d3.scaleBand()
      .domain(data.dates)
      .range([0, innerW])
      .padding(0.3);

    const valMax = d3.max(values, (v) => Math.abs(v)) || 1;
    const yPadding = valMax * 0.15;

    const y = d3.scaleLinear()
      .domain([-(valMax + yPadding), valMax + yPadding])
      .nice()
      .range([innerH, 0]);

    // Acceleration scale (right Y-axis)
    const hasAcceleration = data.acceleration && data.acceleration.some((v) => v !== null && v !== undefined);
    let yAccel = null;
    if (hasAcceleration) {
      const accelValues = data.acceleration.filter((v) => v !== null && v !== undefined);
      const accelMax = d3.max(accelValues, (v) => Math.abs(v)) || 1;
      const accelPadding = accelMax * 0.15;
      yAccel = d3.scaleLinear()
        .domain([-(accelMax + accelPadding), accelMax + accelPadding])
        .nice()
        .range([innerH, 0]);
    }

    // Horizontal grid lines
    const yTicks = y.ticks();
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('class', 'grid-line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Zero line
    g.append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', y(0))
      .attr('y2', y(0))
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 1);

    // X axis
    const formatDate = (dateStr) => {
      const date = new Date(dateStr);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[date.getMonth()]} ${date.getDate()}`;
    };

    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(d3.axisBottom(x).tickSize(0).tickFormat(formatDate))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.5em');

    g.select('.domain').attr('stroke', '#e5e7eb');

    // Y axis left
    const yTickFormat = mode === 'dollar'
      ? (v) => `${v >= 0 ? '+' : ''}$${Math.abs(v).toFixed(2)}`
      : (v) => `${v >= 0 ? '+' : '-'}${Math.abs(v).toFixed(1)}%`;

    g.append('g')
      .call(d3.axisLeft(y).ticks(6).tickFormat(yTickFormat))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Right Y axis for acceleration
    if (hasAcceleration && yAccel) {
      g.append('g')
        .attr('transform', `translate(${innerW}, 0)`)
        .call(
          d3.axisRight(yAccel)
            .ticks(4)
            .tickFormat((v) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}`)
        )
        .selectAll('text')
        .attr('font-size', '10px')
        .attr('fill', ACCEL_COLOR);

      // Right axis label
      g.append('text')
        .attr('transform', `translate(${innerW + 40}, ${innerH / 2}) rotate(-90)`)
        .attr('text-anchor', 'middle')
        .attr('font-size', '10px')
        .attr('fill', ACCEL_COLOR)
        .text('Acceleration');
    }

    g.selectAll('.domain').attr('stroke', '#e5e7eb');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Bars
    const barWidth = x.bandwidth() * 0.6;
    const barOffset = (x.bandwidth() - barWidth) / 2;

    data.dates.forEach((date, i) => {
      const val = values[i];
      if (val === null || val === undefined) return;

      const barX = x(date) + barOffset;
      const isPositive = val >= 0;
      const color = isPositive ? BAR_COLORS.positive : BAR_COLORS.negative;

      const barTop = isPositive ? y(val) : y(0);
      const barBottom = isPositive ? y(0) : y(val);
      const barHeight = Math.max(1, barBottom - barTop);

      g.append('rect')
        .attr('x', barX)
        .attr('y', barTop)
        .attr('width', barWidth)
        .attr('height', barHeight)
        .attr('fill', color)
        .attr('fill-opacity', 0.8)
        .attr('rx', 2);
    });

    // Acceleration overlay line
    if (hasAcceleration && yAccel) {
      const accelPoints = data.dates
        .map((date, i) => ({
          date,
          value: data.acceleration[i],
        }))
        .filter((d) => d.value !== null && d.value !== undefined);

      if (accelPoints.length > 1) {
        const lineGen = d3.line()
          .x((d) => x(d.date) + x.bandwidth() / 2)
          .y((d) => yAccel(d.value))
          .curve(d3.curveMonotoneX);

        g.append('path')
          .datum(accelPoints)
          .attr('d', lineGen)
          .attr('fill', 'none')
          .attr('stroke', ACCEL_COLOR)
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', '4,3');

        // Dots on the acceleration line
        accelPoints.forEach((d) => {
          g.append('circle')
            .attr('cx', x(d.date) + x.bandwidth() / 2)
            .attr('cy', yAccel(d.value))
            .attr('r', 3)
            .attr('fill', ACCEL_COLOR)
            .attr('stroke', '#fff')
            .attr('stroke-width', 1.5);
        });
      }
    }

    // Invisible hover targets
    data.dates.forEach((date, i) => {
      g.append('rect')
        .attr('x', x(date))
        .attr('y', 0)
        .attr('width', x.bandwidth())
        .attr('height', innerH)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            date,
            velocity: data.velocity ? data.velocity[i] : null,
            velocityPercent: data.velocityPercent ? data.velocityPercent[i] : null,
            acceleration: data.acceleration ? data.acceleration[i] : null,
          });
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, mode]);

  if (!data || !data.dates || data.dates.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.headerText}>
            <div style={styles.title}>Price Velocity</div>
            <div style={styles.subtitle}>Rate of average pest365 price change between scrape dates</div>
          </div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No velocity data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.headerText}>
          <div style={styles.title}>Price Velocity</div>
          <div style={styles.subtitle}>Rate of average pest365 price change between scrape dates</div>
        </div>
        <div style={styles.toggleGroup}>
          <button
            style={{
              ...styles.toggleButton,
              ...(mode === 'dollar' ? styles.toggleActive : styles.toggleInactive),
            }}
            onClick={() => setMode('dollar')}
          >
            $
          </button>
          <button
            style={{
              ...styles.toggleButton,
              ...(mode === 'percent' ? styles.toggleActive : styles.toggleInactive),
            }}
            onClick={() => setMode('percent')}
          >
            %
          </button>
        </div>
      </div>
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <div style={{ width: 12, height: 10, background: BAR_COLORS.positive, borderRadius: 1, opacity: 0.8 }} />
          <span>Rising</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ width: 12, height: 10, background: BAR_COLORS.negative, borderRadius: 1, opacity: 0.8 }} />
          <span>Falling</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="16" height="10">
            <line x1="0" y1="5" x2="16" y2="5" stroke={ACCEL_COLOR} strokeWidth="2" strokeDasharray="4,3" />
          </svg>
          <span>Acceleration</span>
        </div>
      </div>
      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {(() => {
              const date = new Date(tooltip.date);
              const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
            })()}
          </div>
          {tooltip.velocity !== null && tooltip.velocity !== undefined && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
              <span style={{ color: '#6b7280' }}>Velocity ($)</span>
              <span
                style={{
                  fontWeight: 600,
                  color: tooltip.velocity >= 0 ? BAR_COLORS.positive : BAR_COLORS.negative,
                }}
              >
                {tooltip.velocity >= 0 ? '+' : ''}${tooltip.velocity.toFixed(2)}
              </span>
            </div>
          )}
          {tooltip.velocityPercent !== null && tooltip.velocityPercent !== undefined && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
              <span style={{ color: '#6b7280' }}>Velocity (%)</span>
              <span
                style={{
                  fontWeight: 600,
                  color: tooltip.velocityPercent >= 0 ? BAR_COLORS.positive : BAR_COLORS.negative,
                }}
              >
                {tooltip.velocityPercent >= 0 ? '+' : ''}{tooltip.velocityPercent.toFixed(2)}%
              </span>
            </div>
          )}
          {tooltip.acceleration !== null && tooltip.acceleration !== undefined && (
            <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '4px', paddingTop: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                <span style={{ color: ACCEL_COLOR }}>Acceleration</span>
                <span
                  style={{
                    fontWeight: 600,
                    color: ACCEL_COLOR,
                  }}
                >
                  {tooltip.acceleration >= 0 ? '+' : ''}{tooltip.acceleration.toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
